import { CanadianProvincesWithISOCodes, getCountryName } from '@/utils/address';
import { GeoArea } from '@/types/GeoArea';
import { normalizeForUrl } from '@/utils/urls';
import CountriesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/CountriesWithISOCodes';
import USStatesWithISOCodes from '@liveauctioneers/caterwaul-components/lib/Intl/USStatesWithISOCodes';

export enum GeoAreaType {
    City = 'city',
    Country = 'country',
    State = 'state',
}

/**
 * Get the 2-letter state/province code from a state/province name
 * Supports US states and Canadian provinces
 */
export const getStateCodeFromName = (stateName: string): string => {
    if (!stateName) {
        return '';
    }

    const US_STATE_CODE_LENGTH = 2;
    let state: { code: string; name: string } | undefined;

    // Check if it's already a 2-letter code
    if (stateName.length === US_STATE_CODE_LENGTH) {
        // Try US states first
        state = USStatesWithISOCodes.find((s) => s.code.toLowerCase() === stateName.toLowerCase());
        // Try Canadian provinces
        if (!state) {
            state = CanadianProvincesWithISOCodes.find((s) => s.code.toLowerCase() === stateName.toLowerCase());
        }
    } else {
        // Try US states by name
        state = USStatesWithISOCodes.find((s) => s.name.toLowerCase() === stateName.toLowerCase());
        // Try Canadian provinces by name
        if (!state) {
            state = CanadianProvincesWithISOCodes.find((s) => s.name.toLowerCase() === stateName.toLowerCase());
        }
    }

    return state?.code.toLowerCase() || '';
};

/**
 * Get country name from 3-letter code for URL slugs
 */
export const getCountrySlugFromCode = (countryCode: string): string => {
    const countryName = getCountryName(countryCode);
    return normalizeForUrl(countryName);
};

/**
 * Get 3-letter country code from URL slug
 */
export const getCountryCodeFromSlug = (slug: string): string | undefined => {
    const country = CountriesWithISOCodes.find((c) => normalizeForUrl(c.name) === slug.toLowerCase());
    return country?.code3;
};

/**
 * Determine the type of geo area (country, state, or city)
 */
export const getGeoAreaType = (geoArea: GeoArea): GeoAreaType => {
    if (geoArea.locality) {
        return GeoAreaType.City;
    }
    if (geoArea.administrativeAreaLevel1) {
        return GeoAreaType.State;
    }
    return GeoAreaType.Country;
};

/**
 * Convert a GeoArea to URL path segments
 * @example
 * Country: { countryName: 'USA', ... } → '/united-states'
 * State: { administrativeAreaLevel1: 'Colorado', countryName: 'USA', ... } → '/colorado'
 * City: { locality: 'Denver', administrativeAreaLevel1: 'Colorado', countryName: 'USA', ... } → '/co/denver'
 */
export const geoAreaToUrlPath = (geoArea: GeoArea): string => {
    const type = getGeoAreaType(geoArea);

    switch (type) {
        case 'country': {
            const countrySlug = getCountrySlugFromCode(geoArea.countryName);
            return `/${countrySlug}`;
        }
        case 'state': {
            // For states, use the state name as the slug
            const stateSlug = normalizeForUrl(geoArea.administrativeAreaLevel1 || geoArea.geoAreaName);
            return `/${stateSlug}`;
        }
        case 'city': {
            // For cities, use state/city format
            const citySlug = normalizeForUrl(geoArea.locality);
            const stateCode = getStateCodeFromName(geoArea.administrativeAreaLevel1);
            // Handle cities without state codes (e.g., UK cities) - use city-only path
            if (!stateCode) {
                return `/${citySlug}`;
            }
            return `/${stateCode}/${citySlug}`;
        }
        default: {
            // Fallback to using geoAreaName
            return `/${normalizeForUrl(geoArea.geoAreaName)}`;
        }
    }
};

/**
 * Find a matching GeoArea from URL parameters
 * This helps match URL params back to a GeoArea for getting bounds
 *
 * Note: URLs use state codes (e.g., "co") but GeoAreas store full state names (e.g., "Colorado")
 * So we convert the stored state name to a code before comparing
 */
export const findGeoAreaFromUrlParams = (
    allGeoAreas: GeoArea[],
    params: { city?: string; country?: string; state?: string }
): GeoArea | undefined => {
    // City + State match
    if (params.city && params.state) {
        const result = allGeoAreas.find((area) => {
            const cityMatch = normalizeForUrl(area.locality) === params.city?.toLowerCase();
            // Convert stored state name to code for comparison (e.g., "Colorado" → "co")
            const stateCodeFromArea = getStateCodeFromName(area.administrativeAreaLevel1);
            const stateMatch = stateCodeFromArea?.toLowerCase() === params.state?.toLowerCase();

            return cityMatch && stateMatch;
        });

        return result;
    }

    // State match (state-only URLs use full state name, e.g., /colorado)
    // If only state param provided (no city, no country), try state first, then country as fallback
    if (params.state && !params.city && !params.country) {
        const stateMatch = allGeoAreas.find((area) => {
            const stateMatch = normalizeForUrl(area.administrativeAreaLevel1) === params.state?.toLowerCase();
            const isState = !area.locality && area.administrativeAreaLevel1;
            return stateMatch && isState;
        });

        // If state match found, return it
        if (stateMatch) {
            return stateMatch;
        }

        // If no state match, try as country (handles ambiguous routes like /australia)
        const countryCode = getCountryCodeFromSlug(params.state);
        if (countryCode) {
            const countryMatch = allGeoAreas.find((area) => {
                const countryMatch = area.countryName === countryCode;
                const isCountry = !area.locality && !area.administrativeAreaLevel1;
                return countryMatch && isCountry;
            });
            return countryMatch;
        }
        return undefined;
    }

    // State match when explicitly provided (with city or country params present)
    if (params.state) {
        return allGeoAreas.find((area) => {
            const stateMatch = normalizeForUrl(area.administrativeAreaLevel1) === params.state?.toLowerCase();
            const isState = !area.locality && area.administrativeAreaLevel1;
            return stateMatch && isState;
        });
    }

    // Country match
    if (params.country) {
        const countryCode = getCountryCodeFromSlug(params.country);
        if (countryCode) {
            const result = allGeoAreas.find((area) => {
                const countryMatch = area.countryName === countryCode;
                const isCountry = !area.locality && !area.administrativeAreaLevel1;
                return countryMatch && isCountry;
            });
            return result;
        }
    }

    return undefined;
};
